import { useState, useEffect, useRef, useMemo } from "react";
import {
  AsYouType,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "libphonenumber-js";
import { COUNTRIES, DEFAULT_COUNTRY } from "../lib/country-codes";

/**
 * Controlled phone input with a country selector.
 *
 * onChange receives a single object:
 *   {
 *     e164:        "+919876543210" | "",   // store THIS — already full E.164
 *     national:    "9876543210",            // digits the user typed
 *     countryCode: "IN",                    // selected ISO country
 *     isValid:     true | false,            // libphonenumber validity
 *   }
 */
export default function PhoneInput({
  value, // optional controlled value: { national, countryCode }
  onChange,
  defaultCountry = DEFAULT_COUNTRY,
  error,
  disabled,
  placeholder = "98765 43210",
  wrapperStyle, // optional overrides for the root .field wrapper (e.g. margin)
}) {
  const [countryCode, setCountryCode] = useState(
    value?.countryCode || defaultCountry,
  );
  const [national, setNational] = useState(value?.national || "");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  const selected = useMemo(
    () => COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0],
    [countryCode],
  );

  // Build the full E.164 from the selected country's calling code + typed
  // digits, and report validity. This is what fixes the wrong-country bug.
  const emit = (nat, country) => {
    const digits = (nat || "").replace(/\D/g, "");
    let e164 = "";
    let isValid = false;
    if (digits) {
      try {
        e164 = `+${getCountryCallingCode(country)}${digits}`;
        isValid = isValidPhoneNumber(e164);
      } catch {
        e164 = "";
        isValid = false;
      }
    }
    onChange?.({ e164, national: digits, countryCode: country, isValid });
  };

  const handleInput = (e) => {
    const typed = e.target.value;

    // If the user pastes a full international number, respect it: adopt its
    // country and strip the prefix into the national field.
    if (typed.trim().startsWith("+")) {
      try {
        const parsed = parsePhoneNumber(typed.trim());
        if (parsed?.country) {
          setCountryCode(parsed.country);
          setNational(parsed.nationalNumber);
          emit(parsed.nationalNumber, parsed.country);
          return;
        }
      } catch {
        /* fall through to national handling */
      }
    }

    const digits = typed.replace(/\D/g, "");
    setNational(digits);
    emit(digits, countryCode);
  };

  const selectCountry = (code) => {
    setCountryCode(code);
    setOpen(false);
    setSearch("");
    emit(national, code); // re-emit E.164 with the new prefix
  };

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = search
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.dialCode.includes(search) ||
          c.code.toLowerCase().includes(search.toLowerCase()),
      )
    : COUNTRIES;

  const display = national ? new AsYouType(countryCode).input(national) : "";

  return (
    <div className="field" ref={ref} style={{ position: "relative", ...wrapperStyle }}>
      {/* Segmented field: compact country button + full-width number input,
          sharing one bordered container (so we avoid .input-group's icon
          padding rule that otherwise squeezes the number). */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          background: "var(--surface-2)",
          border: `1px solid ${
            error ? "var(--error)" : focused ? "var(--primary)" : "var(--border)"
          }`,
          borderRadius: "var(--radius-sm)",
          boxShadow: focused ? "0 0 0 3px var(--primary-glow)" : "none",
          transition: "all var(--trans)",
          overflow: "hidden",
        }}
      >
        {/* Country selector */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            flex: "0 0 auto",
            padding: "11px 10px 11px 14px",
            background: "transparent",
            border: "none",
            borderRight: "1px solid var(--border)",
            color: "var(--text)",
            fontSize: 14,
            lineHeight: 1,
            whiteSpace: "nowrap",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {selected?.dialCode}
          </span>
          <span style={{ opacity: 0.55, fontSize: 11 }}>▾</span>
        </button>

        {/* National number */}
        <input
          type="tel"
          value={display}
          onChange={handleInput}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          required
          style={{
            flex: 1,
            minWidth: 0,
            width: "100%",
            padding: "11px 14px",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--text)",
            fontSize: 14,
          }}
        />
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            zIndex: 50,
            top: "100%",
            left: 0,
            marginTop: 4,
            width: 288,
            maxHeight: 240,
            overflow: "auto",
            background: "var(--surface, #fff)",
            border: "1px solid var(--border, #e5e7eb)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          <div
            style={{
              position: "sticky",
              top: 0,
              padding: 8,
              background: "inherit",
            }}
          >
            <input
              autoFocus
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country…"
              style={{ width: "100%" }}
            />
          </div>
          {filtered.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => selectCountry(c.code)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                padding: "8px 12px",
                textAlign: "left",
                background:
                  c.code === countryCode
                    ? "var(--surface-2, #f3f4f6)"
                    : "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {c.name}
              </span>
              <span style={{ opacity: 0.5, marginLeft: 8 }}>{c.dialCode}</span>
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="field-hint" style={{ color: "var(--error)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
